import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { TaskForm } from './TaskForm';
import { useTaskStore } from '@/app/store/taskStore';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { taskService } from '@/app/services/taskService';
import { TaskCreate } from '@/app/types/task';
import { toast } from 'sonner';

/**
 * CreateTaskModal Component
 *
 * Modal dialog for creating new tasks
 * Integrates with TaskStore for state management and React Query for API calls
 *
 * Opens when useTaskStore.isCreateModalOpen is true
 * Can be pre-filled with party ID from useTaskStore.prefilledPartyId
 */
export const CreateTaskModal: React.FC = () => {
  const queryClient = useQueryClient();
  const { isCreateModalOpen, closeCreateModal, prefilledPartyId, prefilledTaskType } = useTaskStore();

  // Create task mutation
  const createTaskMutation = useMutation({
    mutationFn: (data: TaskCreate) => taskService.create(data),
    onSuccess: (newTask) => {
      const entityLabel = prefilledTaskType === 'appointment' ? 'Appointment' : 'Task';
      toast.success(`${entityLabel} created successfully`, {
        description: `"${newTask.title}" has been added to your ${entityLabel.toLowerCase()}s.`,
      });

      // Invalidate task queries to refetch data
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['taskCounts'] });
      queryClient.invalidateQueries({ queryKey: ['myTasks'] });

      // Close modal
      closeCreateModal();
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to create task';

      toast.error('Failed to create task', {
        description: errorMessage,
      });
    },
  });

  const handleSubmit = (data: TaskCreate) => {
    createTaskMutation.mutate(data);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      closeCreateModal();
    }
  };

  return (
    <Dialog open={isCreateModalOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {prefilledTaskType === 'appointment' ? 'Create New Appointment' : 'Create New Task'}
          </DialogTitle>
          <DialogDescription>
            {prefilledTaskType === 'appointment'
              ? 'Schedule a new appointment for your team.'
              : 'Add a new task to track work, follow-ups, or reminders for your team.'}
          </DialogDescription>
        </DialogHeader>

        <TaskForm
          mode="create"
          onSubmit={handleSubmit}
          isLoading={createTaskMutation.isPending}
          prefilledPartyId={prefilledPartyId}
          prefilledTaskType={prefilledTaskType}
        />
      </DialogContent>
    </Dialog>
  );
};
